import {Fragment, useState} from 'react'
import {withNamespaces, WithNamespaces} from 'react-i18next'
import messages from '../../../assets/messages'
import {OfferNode} from '../types'
import {AgentChainPill, buildAgentChain, collapseAgentChain} from '../utils/agentChain'
import {
  Chain,
  CurrentPill,
  EllipsisPill,
  LinkPill,
  Middot,
  OwnerName,
  PillLevel,
  Separator,
  StaticPill,
} from './AgentChainBreadcrumb.styled'
import {useExpansion} from './ExpansionContext'

type AgentChainBreadcrumbProps = {node: OfferNode} & WithNamespaces

/** Marks the expandable ellipsis in the collapsed pill sequence. */
const ELLIPSIS = 'ellipsis' as const
type ChainEntry = AgentChainPill | typeof ELLIPSIS

/**
 * The Agent Chain breadcrumb shown at the top of a node's KPI panel (PT-5713): the path from the
 * rendered root down to the current node, "Root → … → Me". It gives an agent deep in the tree a clear
 * view of who sits upstream of them and where fees flow.
 *
 * Each upstream pill is a button that scrolls the tree to that ancestor; the current node's pill is
 * emphasised and inert. Chains longer than the display cap collapse their middle behind an ellipsis
 * that expands in place, so even a very deep chain stays to a single readable row.
 *
 * @param node - The node whose KPI panel hosts this breadcrumb (the "Me" end of the chain).
 * @param t - Translation function injected by `withNamespaces`, used for the level and aria labels.
 */
const _AgentChainBreadcrumb = ({node, t}: AgentChainBreadcrumbProps) => {
  const [expanded, setExpanded] = useState(false)
  const {revealAndScroll} = useExpansion()
  const pills = buildAgentChain(node)
  const collapsed = expanded ? null : collapseAgentChain(pills)

  // When collapsed, the middle pills give way to a single expandable ellipsis marker.
  const entries: ChainEntry[] = collapsed ? [collapsed.head, ELLIPSIS, ...collapsed.tail] : pills

  const levelLabel = (level: number): string =>
    t(messages.pammAgentChainLevel.i18nKey, messages.pammAgentChainLevel.defaults, {level})

  const renderEntry = (entry: ChainEntry) => {
    if (entry === ELLIPSIS) {
      return (
        <EllipsisPill
          type="button"
          data-testid="chain-ellipsis"
          aria-label={t(messages.pammAgentChainExpand.i18nKey, messages.pammAgentChainExpand.defaults, {
            count: collapsed?.hidden.length ?? 0,
          })}
          onClick={() => setExpanded(true)}
        >
          …
        </EllipsisPill>
      )
    }

    // "Owner · Lvl N" (AC): owner truncates, the level stays pinned to its right.
    const content = (
      <>
        <OwnerName>{entry.ownerName}</OwnerName>
        <Middot aria-hidden="true">·</Middot>
        <PillLevel>{levelLabel(entry.level)}</PillLevel>
      </>
    )
    const testId = `chain-pill-${entry.offerId ?? 'unknown'}`

    if (entry.isCurrent) {
      return (
        <CurrentPill data-testid={testId} aria-current="location">
          {content}
        </CurrentPill>
      )
    }

    // An upstream ancestor the viewer can reach in the tree is a live scroll target. A pill without
    // a scroll target (no id, or a future node outside the viewer's tree) is shown but inert.
    if (entry.isClickable && entry.offerId != null) {
      const scrollTarget = entry.offerId
      return (
        <LinkPill
          type="button"
          data-testid={testId}
          // Include the level so screen-reader users get the same "owner + level" the pill shows.
          aria-label={`${t(messages.pammAgentChainScrollTo.i18nKey, messages.pammAgentChainScrollTo.defaults, {
            owner: entry.ownerName,
          })}, ${levelLabel(entry.level)}`}
          onClick={() => revealAndScroll(scrollTarget)}
        >
          {content}
        </LinkPill>
      )
    }

    return <StaticPill data-testid={testId}>{content}</StaticPill>
  }

  return (
    <Chain aria-label={t(messages.pammAgentChainHeading.i18nKey, messages.pammAgentChainHeading.defaults)} data-testid="agent-chain-breadcrumb">
      {entries.map((entry, index) => {
        const key = entry === ELLIPSIS ? ELLIPSIS : `pill-${entry.offerId ?? 'unknown'}-${index}`
        return (
          <Fragment key={key}>
            {index > 0 && <Separator aria-hidden="true">→</Separator>}
            {renderEntry(entry)}
          </Fragment>
        )
      })}
    </Chain>
  )
}

export const AgentChainBreadcrumb = withNamespaces()(_AgentChainBreadcrumb)
